import type { SiteLocale } from '@/lib/marketing/page-metadata';

/** Every visible string of the hero CTA (both arms + the live call card), per
 *  site language. The call and the text thread follow the same language: the
 *  call POST carries `lang`, the prefilled greeting comes from heroBody(). */
export interface HeroCtaCopy {
  messageTessa: string;
  messageArmLine: string;
  qrPanelTitle: string;
  qrPanelLabel: string;
  qrAlt: (number: string) => string;
  copyNumber: string;
  copiedNumber: string;
  phonePlaceholder: string;
  callMe: string;
  domainPlaceholder: string;
  draftMyCheckIn: string;
  domainToggle: string;
  backToCall: string;
  preferMessage: string;
  orText: string;
  preferText: string;
  startFromTemplate: string;
  accountExists: { before: string; link: string; after: string };
  networkError: string;
  errors: Record<
    | 'invalid_phone'
    | 'country_not_served'
    | 'phone_type_not_callable'
    | 'captcha_failed'
    | 'rate_limited'
    | 'call_unavailable',
    string
  >;
  status: {
    drafted: string;
    seeDraft: string;
    textedLink: string;
    draftMissing: { before: string; link: string; after: string };
    noAnswer: string;
    failed: string;
    failedLink: string;
    answered: string;
    ringing: string;
  };
}

export const HERO_CTA_COPY: Record<SiteLocale, HeroCtaCopy> = {
  en: {
    messageTessa: 'Message Tessa',
    messageArmLine: 'She replies in seconds and drafts your first check-in from the conversation.',
    qrPanelTitle: 'Scan with your phone — the text is already written',
    qrPanelLabel: 'Message Tessa from your phone',
    qrAlt: (number) => `QR code — text ${number} from your phone`,
    copyNumber: 'Copy',
    copiedNumber: 'Copied',
    phonePlaceholder: 'Your phone — Tessa calls you in ~30 seconds',
    callMe: 'Call me',
    domainPlaceholder: 'Your company domain — e.g. acme.com',
    draftMyCheckIn: 'Draft my check-in',
    domainToggle: 'or paste your company domain — Tessa drafts from it →',
    backToCall: '← back to "Tessa calls me"',
    preferMessage: 'Prefer to message? Say hello on',
    orText: '— or text',
    preferText: 'Prefer to text? Say hello:',
    startFromTemplate: 'start from a template →',
    accountExists: {
      before: 'That number already has an account —',
      link: 'sign in here',
      after: 'and Tessa is one tap away.',
    },
    networkError: 'Network hiccup — try again.',
    errors: {
      invalid_phone: "That number doesn't look complete — check the digits and try again.",
      country_not_served: "We can't call that country yet — but you can build your check-in below.",
      phone_type_not_callable: 'That looks like a business/toll-free line — use your mobile.',
      captcha_failed: "The security check didn't pass — try again.",
      rate_limited: 'Too many tries for now — give it a bit, or build your check-in below.',
      call_unavailable:
        'Calling is briefly unavailable — build your check-in below and Tessa takes it from there.',
    },
    status: {
      drafted: 'Your first check-in is drafted — from your own words.',
      seeDraft: 'See your draft →',
      textedLink: 'We also texted you the link, so nothing is lost if you close this page.',
      draftMissing: {
        before: "Good call! The draft didn't make it here though — ",
        link: 'build your check-in from a template',
        after: ' and it takes ~2 minutes.',
      },
      noAnswer: "Tessa couldn't reach you — try again whenever you're ready.",
      failed: "That call didn't go through. Try again in a moment, or ",
      failedLink: 'build your check-in here instead',
      answered: "You're talking to Tessa — she's drafting your check-in as you speak.",
      ringing: "Your phone is ringing — that's Tessa. Pick up and say hi.",
    },
  },
  es: {
    messageTessa: 'Escríbele a Tessa',
    messageArmLine: 'Te responde en segundos y prepara tu primera conversación a partir del chat.',
    qrPanelTitle: 'Escanéalo con tu celular — el mensaje ya está escrito',
    qrPanelLabel: 'Escríbele a Tessa desde tu celular',
    qrAlt: (number) => `Código QR — escríbele al ${number} desde tu celular`,
    copyNumber: 'Copiar',
    copiedNumber: 'Copiado',
    phonePlaceholder: 'Tu celular — Tessa te llama en ~30 segundos',
    callMe: 'Llámame',
    domainPlaceholder: 'El dominio de tu empresa — p. ej. acme.com',
    draftMyCheckIn: 'Prepara mi conversación',
    domainToggle: 'o pega el dominio de tu empresa — Tessa prepara todo desde ahí →',
    backToCall: '← volver a "Tessa me llama"',
    preferMessage: '¿Prefieres escribir? Salúdala por',
    orText: '— o por SMS al',
    preferText: '¿Prefieres un SMS? Salúdala:',
    startFromTemplate: 'empieza con una plantilla →',
    accountExists: {
      before: 'Ese número ya tiene una cuenta —',
      link: 'inicia sesión aquí',
      after: 'y Tessa está a un toque.',
    },
    networkError: 'Falló la conexión — inténtalo de nuevo.',
    errors: {
      invalid_phone: 'Ese número parece incompleto — revisa los dígitos e inténtalo de nuevo.',
      country_not_served: 'Todavía no podemos llamar a ese país — pero puedes preparar tu conversación abajo.',
      phone_type_not_callable: 'Parece una línea de empresa o sin costo — usa tu celular.',
      captcha_failed: 'La verificación de seguridad no pasó — inténtalo de nuevo.',
      rate_limited: 'Demasiados intentos por ahora — espera un rato, o prepárala abajo.',
      call_unavailable:
        'Las llamadas no están disponibles por un momento — prepara tu conversación abajo y Tessa sigue desde ahí.',
    },
    status: {
      drafted: 'Tu primera conversación está lista — con tus propias palabras.',
      seeDraft: 'Ver tu borrador →',
      textedLink: 'También te mandamos el enlace por mensaje: no pierdes nada si cierras esta página.',
      draftMissing: {
        before: '¡Buena llamada! Pero el borrador no llegó aquí — ',
        link: 'arma tu conversación con una plantilla',
        after: ' y toma unos 2 minutos.',
      },
      noAnswer: 'Tessa no pudo comunicarse contigo — inténtalo de nuevo cuando quieras.',
      failed: 'Esa llamada no entró. Inténtalo en un momento, o ',
      failedLink: 'arma tu conversación aquí',
      answered: 'Estás hablando con Tessa — está preparando tu conversación mientras platican.',
      ringing: 'Está sonando tu teléfono — es Tessa. Contesta y salúdala.',
    },
  },
  fr: {
    messageTessa: 'Écrire à Tessa',
    messageArmLine: 'Elle répond en quelques secondes et prépare votre premier suivi à partir de la conversation.',
    qrPanelTitle: 'Balayez avec votre cellulaire — le message est déjà écrit',
    qrPanelLabel: 'Écrire à Tessa depuis votre cellulaire',
    qrAlt: (number) => `Code QR — textez le ${number} depuis votre cellulaire`,
    copyNumber: 'Copier',
    copiedNumber: 'Copié',
    phonePlaceholder: 'Votre cellulaire — Tessa vous appelle en ~30 secondes',
    callMe: 'Appelez-moi',
    domainPlaceholder: 'Le domaine de votre entreprise — p. ex. acme.com',
    draftMyCheckIn: 'Préparer mon suivi',
    domainToggle: 'ou collez le domaine de votre entreprise — Tessa prépare tout à partir de là →',
    backToCall: '← retour à « Tessa m’appelle »',
    preferMessage: 'Vous préférez écrire? Dites bonjour sur',
    orText: '— ou textez le',
    preferText: 'Vous préférez texter? Dites bonjour :',
    startFromTemplate: 'partez d’un modèle →',
    accountExists: {
      before: 'Ce numéro a déjà un compte —',
      link: 'connectez-vous ici',
      after: 'et Tessa est à portée de main.',
    },
    networkError: 'Problème de connexion — réessayez.',
    errors: {
      invalid_phone: 'Ce numéro semble incomplet — vérifiez les chiffres et réessayez.',
      country_not_served: 'Nous ne pouvons pas encore appeler ce pays — mais vous pouvez préparer votre suivi ci-dessous.',
      phone_type_not_callable: 'Cela ressemble à une ligne d’entreprise ou sans frais — utilisez votre cellulaire.',
      captcha_failed: 'La vérification de sécurité a échoué — réessayez.',
      rate_limited: 'Trop d’essais pour l’instant — patientez un peu, ou préparez-le ci-dessous.',
      call_unavailable:
        'Les appels sont brièvement indisponibles — préparez votre suivi ci-dessous et Tessa prendra le relais.',
    },
    status: {
      drafted: 'Votre premier suivi est prêt — dans vos propres mots.',
      seeDraft: 'Voir votre brouillon →',
      textedLink: 'Nous vous avons aussi texté le lien : rien n’est perdu si vous fermez cette page.',
      draftMissing: {
        before: 'Bon appel! Mais le brouillon n’est pas arrivé ici — ',
        link: 'créez votre suivi à partir d’un modèle',
        after: ', en environ 2 minutes.',
      },
      noAnswer: 'Tessa n’a pas pu vous joindre — réessayez quand vous voulez.',
      failed: 'L’appel n’a pas abouti. Réessayez dans un instant, ou ',
      failedLink: 'créez votre suivi ici',
      answered: 'Vous parlez avec Tessa — elle prépare votre suivi pendant que vous parlez.',
      ringing: 'Votre téléphone sonne — c’est Tessa. Répondez et dites bonjour.',
    },
  },
};
